import React, { createContext, useContext, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { getLocalApiUrl, USER_KEY } from '../config/api'
import { useFeatureFlags } from './FeatureFlagContext'

const AuthContext = createContext({})

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [permissions, setPermissions] = useState(new Set())
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const { setFlagsFromLogin, clearFlags } = useFeatureFlags()

  const hydrateFromUserData = (userData) => {
    setUser(userData)
    setPermissions(new Set(Array.isArray(userData.permissions) ? userData.permissions : []))
    setRoles(Array.isArray(userData.roles) ? userData.roles : userData.role ? [userData.role] : [])
  }

  useEffect(() => {
    const stored = localStorage.getItem(USER_KEY)
    if (stored) {
      try {
        hydrateFromUserData(JSON.parse(stored))
      } catch {
        localStorage.removeItem(USER_KEY)
      }
    }
    setLoading(false)
  }, [])

  const login = async (credentials) => {
    try {
      localStorage.removeItem(USER_KEY)
      const res = await fetch(getLocalApiUrl('/api/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials || {}),
      })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || j.message || 'Login failed')
      const userData = {
        ...j.user,
        token: j.token,
        permissions: j.permissions || [],
        roles: j.roles || (j.user?.role ? [j.user.role] : []),
      }
      hydrateFromUserData(userData)
      localStorage.setItem(USER_KEY, JSON.stringify(userData))
      if (j.featureFlags) setFlagsFromLogin(j.featureFlags)
      return { success: true }
    } catch (e) {
      return { success: false, error: e.message }
    }
  }

  const logout = () => {
    const username = user?.username || user?.sub
    if (user?.token) {
      fetch(getLocalApiUrl('/api/logout'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify(username ? { username } : {}),
      }).catch(() => {})
    }
    setUser(null)
    setPermissions(new Set())
    setRoles([])
    localStorage.removeItem(USER_KEY)
    clearFlags()
    toast.success('Logged out')
    navigate('/login')
  }

  const isAuthenticated = () => !!user

  const hasFullAccessRole = () => {
    if (!user) return false
    if (user.role === 'admin' || user.role === 'super_admin') return true
    if (roles.includes('admin') || roles.includes('super_admin')) return true
    return false
  }

  const can = (resource, action) => {
    if (hasFullAccessRole()) return true
    if (!permissions || permissions.size === 0) return false
    const key = `${resource}:${action}`
    if (permissions.has(key)) return true
    const wildcards = [`${resource}:*`, `*:${action}`, '*:*']
    return wildcards.some((k) => permissions.has(k))
  }

  const hasPermission = (requiredRole) => {
    if (!user) return false
    if (requiredRole && requiredRole.includes(':')) {
      const i = requiredRole.lastIndexOf(':')
      return can(requiredRole.slice(0, i), requiredRole.slice(i + 1))
    }
    if (user.role === 'admin' || user.role === 'super_admin' || roles.includes('admin') || roles.includes('super_admin'))
      return true
    return roles.includes(requiredRole) || user.role === requiredRole
  }

  const value = {
    user,
    login,
    logout,
    isAuthenticated,
    hasPermission,
    can,
    permissions,
    roles,
    loading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthContext
